import type { ApiPaths } from '@reos/api-client';
import { apiClient, baseUrl } from './api.js';
import { unwrap } from './api-error.js';
import type { CaseGeometryCollectionJson } from './parcel-map-geojson.js';

/**
 * Krok M1 — datova vrstva mapove karty. Zadna URL tu neni napsana natvrdo:
 * vrstvy chodi z registru pres `GET /v1/duediligence/map/layers`
 * (`buildClientMapLayers`, `@reos/duediligence`), klik do mapy pres
 * `GET /v1/duediligence/map/parcel-at-point` (K0c resolver na serveru,
 * klient s ČÚZK nikdy nemluví přímo).
 */

export type ClientMapLayerDto =
  ApiPaths['/v1/duediligence/map/layers']['get']['responses']['200']['content']['application/json']['items'][number];

export type ParcelLookupDto =
  ApiPaths['/v1/duediligence/map/parcel-at-point']['get']['responses']['200']['content']['application/json'];

export async function fetchClientMapLayers(): Promise<readonly ClientMapLayerDto[]> {
  const result = await unwrap(apiClient.GET('/v1/duediligence/map/layers', {}));
  return result.items;
}

export async function fetchCaseGeometry(caseId: string): Promise<CaseGeometryCollectionJson> {
  const result = await unwrap(
    apiClient.GET('/v1/duediligence/cases/{id}/geometry.geojson', { params: { path: { id: caseId } } }),
  );
  return result as unknown as CaseGeometryCollectionJson;
}

export async function fetchParcelAtPoint(x: number, y: number): Promise<ParcelLookupDto> {
  return unwrap(apiClient.GET('/v1/duediligence/map/parcel-at-point', { params: { query: { x, y } } }));
}

export type HouseLandNeighboursDto =
  ApiPaths['/v1/duediligence/map/parcel-neighbours']['get']['responses']['200']['content']['application/json'];

/**
 * Krok PRF3 bod 2 — sousedni pozemky stavebni parcely jako NABIDKA pozemku
 * domu. Server je bere z RUIAN (geo-api) a komunikace z nich uz vyradil;
 * o tom, co k domu patri, rozhoduje makler zaskrtnutim (vlastnictvi
 * v otevrenych datech neni).
 */
export async function fetchParcelNeighbours(parcelId: string): Promise<HouseLandNeighboursDto> {
  return unwrap(
    apiClient.GET('/v1/duediligence/map/parcel-neighbours', { params: { query: { parcelId } } }),
  );
}

export type ParcelProximityDto =
  ApiPaths['/v1/duediligence/cases/{id}/map/proximity']['get']['responses']['200']['content']['application/json'];

/**
 * Krok NAP14 bod 1 (seznam A30) — vzdalenosti k plocham a pocty objektu
 * v okoli zamcene parcely pripadu. Server cte zdroje ZIVE, takze se vola az
 * na vyzadani ze sekce panelu, ne pri otevreni mapy.
 */
export async function fetchParcelProximity(caseId: string): Promise<ParcelProximityDto> {
  return unwrap(
    apiClient.GET('/v1/duediligence/cases/{id}/map/proximity', {
      params: { path: { id: caseId } },
    }),
  );
}

/**
 * M1b — `<img src>` mísí obrázek jako binární GET (cookie session jde
 * automaticky, stejný origin), ne přes `apiClient` (ten je jen pro JSON).
 * `yaw` = "point" (výchozí, kamera na parcelu) nebo kompasový směr 0/90/180/270.
 */
export function panoramaImageUrl(caseId: string, yaw: 'point' | 0 | 90 | 180 | 270 = 'point'): string {
  return `${baseUrl}/v1/duediligence/cases/${caseId}/panorama.jpg?yaw=${yaw}`;
}
